/**
 * Device Profile Detail Component
 *
 * Read-only detail view for a single device profile.
 * Mirrors the OrganizationDetail look: back-button row with an Edit button,
 * a dark ProfileHeader hero, and dark ProfileSection cards.
 */

import { Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ProfileHeader, ProfileSection } from '@/components/sensnet/ProfileLayout'
import { ArrowLeft, Loader2, Edit, Layers, Settings2 } from 'lucide-react'
import { useApiQuery } from '@/api/ApiQueryServiceProvider'

export interface DeviceProfileDetailProps {
  deviceProfileNumber: number
}

export function DeviceProfileDetail({ deviceProfileNumber }: DeviceProfileDetailProps) {
  const navigate = useNavigate()

  const { data, isLoading } = useApiQuery<any>('getDeviceProfile', { deviceProfileNumber })
  const profile = data?.content?.deviceProfile

  const handleBack = () => navigate({ to: '/sensory/device-profiles' })

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading device profile details...</span>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Device Profile not found.</p>
        <Button variant="outline" onClick={handleBack}>Back to Device Profiles</Button>
      </div>
    )
  }

  // LoRa attributes may be nested depending on network type.
  const loraAttrs = profile.deviceProfileLoraAttributes || profile.deviceProfileAttributes || {}
  const region = loraAttrs.region ?? profile.region
  const macVersion = loraAttrs.macVersion ?? profile.macVersion
  const supportsOtaa = loraAttrs.deviceSupportsOtaa ?? profile.supportsOtaa
  const supportsClassB = loraAttrs.devicesupportsClassB ?? profile.supportsClassB

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2 -ml-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button asChild size="sm" className="gap-2">
          <Link
            to="/sensory/device-profiles/$deviceProfileNumber/edit"
            params={{ deviceProfileNumber: String(profile.deviceProfileNumber) }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </Link>
        </Button>
      </div>

      <ProfileHeader
        title={profile.deviceProfileName}
        subtitle={`Device Profile Number: ${profile.deviceProfileNumber}`}
        subtitleIcon={<Layers className="h-4 w-4" />}
        badge={profile.deviceProfileNetwork}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProfileSection title="General Information" icon={<Layers className="h-4 w-4" />} variant="dark">
          <div className="space-y-3 p-5">
            <div>
              <p className="text-xs text-muted-foreground">Profile Name</p>
              <p className="text-sm">{profile.deviceProfileName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Profile Number</p>
              <p className="text-sm">{profile.deviceProfileNumber}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Network</p>
              <p className="text-sm">{profile.deviceProfileNetwork || '—'}</p>
            </div>
          </div>
        </ProfileSection>

        <ProfileSection title="LoRa Configuration" icon={<Settings2 className="h-4 w-4" />} variant="dark">
          <div className="space-y-3 p-5">
            <div>
              <p className="text-xs text-muted-foreground">Region</p>
              <p className="text-sm">{region || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">MAC Version</p>
              <p className="text-sm">{macVersion || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Supports OTAA</p>
              <p className="text-sm">{supportsOtaa ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Supports Class B</p>
              <p className="text-sm">{supportsClassB ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </ProfileSection>
      </div>
    </div>
  )
}
